<template>
  <div class="container">
    <b-loading :is-full-page="false" v-model="loadingTopic"></b-loading>
    <b-loading :is-full-page="true" v-model="loadingSubscribe"></b-loading>

    <div v-if="selectedForum && selectedTopic">
      <div class="custom-panel">

        <div class="topic-info-panel">
          <div class="mb-3 has-text-centered">
            <username :user="selectedTopic.user" size="medium" />
            <br />
            <forum-date :date="selectedTopic.created_at" icon />
          </div>
          <div v-if="selectedTopic.forum.slug === 'bugs'" class="custom-panel custom-panel-grey">
            <div class="status-label"><strong>Status:</strong></div>
            <div class="status-tag"><b-tag size="is-small" :type="getStatusClass(selectedTopic.status)">{{ selectedTopic.status }}</b-tag></div>
            <div class="is-clearfix"></div>
            <div>
              <small v-if="selectedTopic.status_text" class="has-text-grey">
                <hr style="margin: 16px 0;" />
                {{ selectedTopic.status_text }}
              </small>
            </div>
          </div>
          <div v-if="canDelete">
            <b-button
              @click="deleteTopic"
              type="is-danger"
              size="is-small"
              icon-left="times"
            >
              Delete Topic
            </b-button>
          </div>
        </div>

        <h5 class="title is-5">
          {{ selectedTopic.title }}
        </h5>
        <h6 class="subtitle is-7">
          in <router-link :to="`/forums/${selectedForum.slug}`"><strong>{{ selectedForum.title }}</strong></router-link>
        </h6>

        <div class="topic-content content" v-html="selectedTopic.text"></div>

        <photo-list
          v-if="selectedTopic.photos.length"
          :photos="selectedTopic.photos"
          class="mt-4"
        ></photo-list>

        <div class="is-clearfix"></div>

      </div>

      <div class="columns mt-6">
        <div class="column is-6">
          <h6 v-if="comments && comments.data && comments.data.length" class="title is-6">Replies</h6>
        </div>
        <div v-if="isAuthenticated" class="column is-6 has-text-right">
          Subscribe to topic:
          <a @click="subscribeMail">
            <b-icon icon="envelope" size="is-small" />
          </a>
          <a :href="`https://api.oddgiants.com/public/forums/${selectedForum.slug}/topics/${selectedTopic.id}/feed`" target="_blank">
            <b-icon icon="rss" size="is-small" style="color: #EE802F;" />
          </a>
        </div>
      </div>

      <comment-list :comments="comments.data" @deleted="commentDeleted"></comment-list>

      <b-pagination
        v-if="comments && comments.data && comments.data.length"
        class="mt-4"
        v-model="comments.current_page"
        :total="comments.total"
        :per-page="comments.per_page"
        size="is-small"
        range-before="3"
        range-after="3"
        order="is-centered"
        icon-pack="fas"
        @change="handlePageChange"
        aria-next-label="Next page"
        aria-previous-label="Previous page"
        aria-page-label="Page"
        aria-current-label="Current page">
      </b-pagination>

      <comment-form v-if="!selectedTopic.closed"></comment-form>
      <b-message v-else type="is-warning" has-icon>
        The comments are closed!
        <p v-if="selectedTopic.closed_reason">
          <small>{{ selectedTopic.closed_reason }}</small>
        </p>
      </b-message>
    </div>
  </div>
</template>

<script>
import CommentList from '@/components/Forum/CommentList.vue';
import CommentForm from '@/components/Forum/CommentForm.vue';
import { mapGetters, mapState } from 'vuex';
import { classByStatus } from '@/helpers/bug-status';
import PhotoList from '@/components/Forum/PhotoList.vue';
import ForumDate from '@/components/Forum/ForumDate.vue';
import Username from '@/components/Forum/Username.vue';
import { Forum } from '@/services/api';

export default {
  name: 'TopicDetails',
  components: {
    ForumDate,
    PhotoList,
    CommentForm,
    CommentList,
    Username,
  },
  props: {
    forum: String,
    topic: String,
  },
  computed: {
    ...mapGetters('user', ['isAuthenticated', 'profile']),
    ...mapGetters('forum', ['isAnnouncements']),
    ...mapState('forum', ['loadingTopic', 'selectedTopic', 'selectedForum', 'comments']),
    isMyTopic() {
      return this.isAuthenticated && this.selectedTopic && this.selectedTopic.user.id === this.profile.id;
    },
    canDelete() {
      return this.isMyTopic && this.selectedTopic.forum.slug !== 'bugs';
    },
  },
  data() {
    return {
      loadingSubscribe: false,
      pageNum: 1,
    };
  },
  mounted() {
    this.loadTopic(this.forum, this.topic);
  },
  beforeRouteUpdate(to, from, next) {
    this.loadTopic(to.params.forum, to.params.topic);
    next();
  },
  methods: {
    async loadTopic(forumSlug, topicId) {
      await this.$store.dispatch('forum/loadForum', forumSlug);
      await this.$store.dispatch('forum/loadTopic', topicId);

      this.pageNum = this.$route.query.page || 1;
      this.$store.dispatch('forum/loadComments', this.pageNum);
    },
    handlePageChange(pageNum) {
      this.$router.push({ path: `/forums/${this.forum}/${this.topic}?page=${pageNum}` });
    },
    getStatusClass(status) {
      return classByStatus(status);
    },
    commentDeleted() {
      this.$store.dispatch('forum/loadComments', this.pageNum);
    },
    deleteTopic() {
      this.$buefy.dialog.confirm({
        title: 'Delete?',
        message: 'Are you sure you want to delete that topic? All its comments will be deleted as well. This action is not revertible.',
        confirmText: 'Yes, delete!',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          this.loadingDelete = true;

          Forum.deleteTopic(this.topic)
            .then(() => {
              this.$router.push({ path: '/forums' });
            })
            .catch((error) => {
              const responseError = error.response.data;

              this.$buefy.dialog.alert({
                title: 'Error',
                message: responseError.message || 'Sorry, something went wrong :( Please try again later...',
                type: 'is-danger',
                hasIcon: true,
                icon: 'times-circle',
                iconPack: 'fa',
                ariaRole: 'alertdialog',
                ariaModal: true,
              });
            })
            .finally(() => {
              this.loadingDelete = false;
            });
        },
      });
    },
    subscribeMail() {
      this.$buefy.dialog.confirm({
        title: 'Subscribe?',
        message: `You will receive emails for every new comment, added to the "${this.selectedTopic.title}" topic. You can always unsubscribe by following the unsubscribe link in the emails.`,
        confirmText: 'Subscribe',
        type: 'is-info',
        hasIcon: true,
        onConfirm: () => {
          this.loadingSubscribe = true;

          Forum.subscribeTopic(this.selectedForum.slug, this.selectedTopic.id)
            .then(() => {
              this.$buefy.dialog.alert({
                title: 'Subscribed!',
                message: 'Thank you! You will receive email notifications when someone leaves a reply on the topic.',
                type: 'is-success',
                hasIcon: true,
                icon: 'check',
                iconPack: 'fa',
                ariaRole: 'alertdialog',
                ariaModal: true,
              });
            })
            .catch((error) => {
              const responseError = error.response.data;

              this.$buefy.dialog.alert({
                title: 'Error',
                message: responseError.message || 'Sorry, something went wrong :( Please try again later...',
                type: 'is-danger',
                hasIcon: true,
                icon: 'times-circle',
                iconPack: 'fa',
                ariaRole: 'alertdialog',
                ariaModal: true,
              });
            })
            .finally(() => {
              this.loadingSubscribe = false;
            });
        },
      });
    },
  },
  metaInfo() {
    return {
      title: this.selectedTopic ? `${this.selectedTopic.title} | Forum` : 'Forum',
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: `${this.selectedTopic ? this.$options.filters.excerpt(this.selectedTopic.text) : 'Topic details'} | Odd Giants Game - developing a new social MMORPG bringing back the beauty of Glitch the Game`,
        },
        {
          vmid: 'keywords',
          name: 'keywords',
          content: 'forums, bugs, topics, comments, odd, giants, glitch, game, remake, multiplayer, online, mmorpg',
        },
      ],
    };
  },
};
</script>

<style scoped lang="scss">
.topic-info-panel {
  float: right;
  margin-left: 16px;
  margin-bottom: 16px;

  .status-label {
    float: left;
    margin-right: 16px;
  }

  .status-tag {
    float: right;
  }
}

@media screen and (max-width: 600px) {
  .topic-info-panel {
    float: none;
  }
}
</style>
